import {
  Badge,
  Box,
  Button,
  Combobox,
  createListCollection,
  Flex,
  HStack,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { DrawerComponent } from '@themes/aegix';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCog6Tooth } from 'react-icons/hi2';
import {
  useActiveOrganization,
  useIsVendorMasqueradeAvailable,
} from '@aegix/auth-organization';
import { useUser } from '@aegix/auth-identity';
import { useSetActiveOrganization } from '../hooks/useSetActiveOrganization';
import { useVendorOnboardingOrganizations } from '../hooks/queries';
import { useCanManageOrganizations } from '../hooks/useCanManageOrganizations';
import { useHasCrossOrgAffiliations } from '../hooks/useHasCrossOrgAffiliations';
import type { ActiveOrganization, SMSUser } from '../lib/sms-auth';
import { OrganizationsContent } from './admin';

/**
 * Organization selector for vendor / Aegix-staff users, and plain users
 * with cross-org affiliations.
 *
 * Shown in the header bar. Lets vendor users pick which onboarding org they
 * are masquerading as, and plain users with site assignments spanning multiple
 * organizations switch between their accessible orgs. Per ADR-114 Phase 3,
 * the dropdown also exposes a permission-gated "Manage organizations…" affordance for staff.
 *
 * Aegix-internal users (perm `accounts.can_access_all_onboarding_orgs`)
 * receive every District in `status='onboard'`, which can be a long list.
 * The Combobox provides type-to-filter; the Manage entry renders as a
 * sticky footer outside the filtered collection so it stays visible
 * regardless of scroll or search state. Manage opens the `OrganizationsContent`
 * surface inside an aegix-themed Drawer instead of navigating away.
 *
 * `searchText` is *only* the filter — it never populates the
 * trigger display. The trigger's `inputValue` is derived from the open
 * state: when closed, it shows `activeOrganization?.name`; when open, it
 * shows `searchText` (which starts empty so the full list is visible).
 * Otherwise the selected org's name would stay in the filter on reopen
 * and narrow the list to just that org.
 */
export function VendorOrgSelector() {
  const { t } = useTranslation();
  const isVendorUser = useIsVendorMasqueradeAvailable();
  const user = useUser<SMSUser>();
  const activeOrganization =
    useActiveOrganization() as ActiveOrganization | null;
  const setActiveOrganization = useSetActiveOrganization();
  const { data: orgs, isLoading } = useVendorOnboardingOrganizations();
  const { canSwitchOrg } = useHasCrossOrgAffiliations();
  const canManage = useCanManageOrganizations();

  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const items = useMemo(
    () =>
      (orgs ?? []).map((org) => ({
        label: org.name,
        value: String(org.id),
        isMember: user?.district != null && org.id === user.district,
      })),
    [orgs, user?.district],
  );

  const filteredItems = useMemo(() => {
    if (!open || !searchText) return items;
    const q = searchText.toLowerCase();
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [items, searchText, open]);

  const collection = useMemo(
    () => createListCollection({ items: filteredItems }),
    [filteredItems],
  );

  if (!canSwitchOrg || (!isVendorUser && isLoading)) return null;

  // Display rule: closed → show the active org's name; open → show what
  // the user is typing (`searchText`). The two roles never share a value,
  // so reopening never pre-filters the list to the selected org.
  const triggerInputValue = open
    ? searchText
    : (activeOrganization?.name ?? '');

  const handleOpenChange = (e: { open: boolean }) => {
    setOpen(e.open);
    if (!e.open) {
      setSearchText('');
    }
  };

  const handleValueChange = (selected: string[]) => {
    const id = selected[0];
    if (!id) {
      setActiveOrganization(null);
      setSearchText('');
      setOpen(false);
      return;
    }
    const match = (orgs ?? []).find((o) => String(o.id) === id);
    if (match) {
      setActiveOrganization(match as ActiveOrganization);
      setSearchText('');
      setOpen(false);
    }
  };

  const openManageDrawer = () => {
    setOpen(false);
    setManageOpen(true);
  };

  return (
    <Box
      px={6}
      py={3}
      bg="surface.primary"
      borderBottomWidth="1px"
      borderColor="border.primary"
    >
      <HStack gap={3} maxW="400px">
        <Text
          fontSize="sm"
          fontWeight="medium"
          color="text.secondary"
          whiteSpace="nowrap"
        >
          {t('organization', 'Organization')}:
        </Text>
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <Combobox.Root
            collection={collection}
            value={activeOrganization ? [String(activeOrganization.id)] : []}
            inputValue={triggerInputValue}
            open={open}
            openOnClick
            onOpenChange={handleOpenChange}
            onValueChange={(e) => handleValueChange(e.value)}
            onInputValueChange={(e) => {
              if (open) setSearchText(e.inputValue);
            }}
            width="100%"
          >
            <Combobox.Control>
              <Combobox.Input
                aria-label={t('organization', 'Organization')}
                placeholder={t('select_organization', 'Select organization…')}
                data-e2e="vendorOrgSelectorTrigger"
              />
              <Combobox.IndicatorGroup>
                <Combobox.ClearTrigger data-e2e="vendorOrgSelectorClear" />
                <Combobox.Trigger />
              </Combobox.IndicatorGroup>
            </Combobox.Control>
            <Portal>
              <Combobox.Positioner zIndex={4300}>
                <Combobox.Content
                  minWidth="280px"
                  bg="surface.primary"
                  border="1px solid"
                  borderColor="border.secondary"
                  borderRadius="md"
                  overflow="hidden"
                  zIndex={4300}
                  data-e2e="vendorOrgSelectorMenu"
                >
                  <Box maxH="320px" overflowY="auto">
                    <Combobox.Empty>
                      <Text px={3} py={2} fontSize="sm" color="text.secondary">
                        {t(
                          'no_matching_organizations',
                          'No matching organizations',
                        )}
                      </Text>
                    </Combobox.Empty>
                    {collection.items.map((item) => (
                      <Combobox.Item
                        key={item.value}
                        item={item}
                        fontSize="14px"
                        fontWeight={
                          item.value === String(activeOrganization?.id)
                            ? 600
                            : 400
                        }
                        data-e2e={`vendorOrgSelectorOrg-${item.value}`}
                      >
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                          gap={2}
                        >
                          <Text truncate>{item.label}</Text>
                          {item.isMember && (
                            <Badge
                              size="xs"
                              variant="subtle"
                              colorPalette="blue"
                              data-e2e="memberOrgBadge"
                            >
                              {t('member_org_badge', 'Member')}
                            </Badge>
                          )}
                        </Flex>
                        <Combobox.ItemIndicator />
                      </Combobox.Item>
                    ))}
                  </Box>
                  {canManage && (
                    <Button
                      variant="ghost"
                      width="100%"
                      justifyContent="flex-start"
                      px={3}
                      py={2}
                      height="auto"
                      borderTopWidth="1px"
                      borderColor="border.secondary"
                      borderRadius={0}
                      bg="surface.primary"
                      _hover={{ bg: 'surface.secondary' }}
                      onClick={openManageDrawer}
                      data-e2e="vendorOrgSelectorManage"
                    >
                      <Flex alignItems="center" gap={2}>
                        <HiCog6Tooth size={16} />
                        <Text
                          fontSize="14px"
                          fontWeight={500}
                          color="text.action"
                        >
                          {t('manage_organizations', 'Manage organizations…')}
                        </Text>
                      </Flex>
                    </Button>
                  )}
                </Combobox.Content>
              </Combobox.Positioner>
            </Portal>
          </Combobox.Root>
        )}
      </HStack>

      {canManage && (
        <DrawerComponent
          isOpen={manageOpen}
          onClose={() => setManageOpen(false)}
          title={t('organizations', 'Organizations')}
          size="xl"
          e2eTitle="manageOrganizationsDrawer"
        >
          <OrganizationsContent />
        </DrawerComponent>
      )}
    </Box>
  );
}
